import { useState, type ReactElement } from 'react';

import { Trash } from 'lucide-react';

import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { i18n } from '../../../../i18n/index.js';

import { Badge, Card, CardContent, CardHeader, CardTitle } from '@govard/ui-primitives';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

import { useForgetMemoryMutation } from '../../../memory/hooks/useMemoryMutations.js';

interface MemorySnippetProps {
  memory: Extract<StructuredMessagePayload, { type: 'memory' }>['memory'];
}

export function MemorySnippet({ memory }: MemorySnippetProps): ReactElement | null {
  useI18nSubscription();
  const [removed, setRemoved] = useState(false);
  const forgetMutation = useForgetMemoryMutation();

  const handleForget = (): void => {
    if (memory.id === undefined) {
      return;
    }
    void forgetMutation.mutateAsync(memory.id).then(() => setRemoved(true));
  };

  if (removed) {
    return null;
  }

  return (
    <Card data-testid="structured-snippet-memory">
      <CardHeader>
        <CardTitle>
          {memory.key ??
            i18n.t('crm.features.features.ai.chat.components.snippets.memorysnippet.pamyat')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {memory.level ? <Badge variant="outline">{memory.level}</Badge> : null}
        <p className="whitespace-pre-wrap">{memory.valueText}</p>
        {memory.id !== undefined && (
          <div className="flex items-center pt-1">
            <button
              type="button"
              data-testid={`memory-snippet-forget-${memory.id}`}
              onClick={handleForget}
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-stone-500 hover:bg-stone-100 hover:text-rose-800 transition cursor-pointer"
            >
              <Trash size={11} className="text-rose-600" />
              <span>{i18n.t('crm.memory.actions.forget')}</span>
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
